import React, { useEffect, useState } from 'react';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { colors } from '@skyslope/mache';
import useUserInfo from '../../auth/useUserInfo';
import CollapseOnScroll from '../../common/CollapseOnScroll';
import type { LaunchDarklyFlags } from '../../common/launchDarkly';
import { SkyslopeAppsMenu } from '../../common/navigation/SkyslopeAppsMenu';
import UserMenu from '../../common/navigation/UserMenu';
import StyledTooltip from '../../common/StyledTooltip';
import { getUserAdminRoles } from '../../common/utils';
import { getUsersGroups } from '../../lib/api/account/accountApi';
import { ResourceNames } from '../../lib/constants';
import { preferencesUrl } from '../../lib/environment-config';
import { UserOrigins } from '../../lib/types';

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    height: '100%',
  },
  headerNav: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
    marginLeft: 10,
  },
  navLink: {
    fontSize: 16,
    color: colors.grey[800],
    '&:hover,&:focus': {
      textDecoration: 'none',
    },
    padding: '10px 15px',
  },
  selectedNavLink: {
    fontWeight: 'bold',
  },
  unselectedNavLink: {
    '&:hover,&:focus': {
      cursor: 'pointer',
      backgroundColor: colors.blue[50],
      borderRadius: '20px',
      outline: 'none',
    },
  },
  navTitle: {
    marginBottom: 16,
  },
  navContent: {
    marginLeft: 24,
  },
  navLinks: {
    display: 'flex',
    flex: '1',
    alignItems: 'center',
  },
  navApps: {
    display: 'flex',
    flex: '1',
    justifyContent: 'flex-end',
    alignItems: 'center',
    fontSize: '14px',
    letterSpacing: '.4px',
    color: colors.grey[800],
  },
  tooltip: {
    marginTop: 0,
  },
}));

interface IProps {
  children?: any;
  resource: string;
  flags?: LaunchDarklyFlags;
  userOrigin?: UserOrigins;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const transformedString = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1) + 's';
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const ManagementHeader = (props: IProps) => {
  const { children, resource, flags, userOrigin } = props;
  const { senderFirstName, senderLastName } = useUserInfo() ?? {};
  const classes = useStyles();
  const transformedResource = transformedString(resource);
  const areTemplatesEnabled = flags && flags['enable-templates-access'];
  const hideNavItems =
    (flags && flags['hide-build-ui-elements']) ||
    (userOrigin !== UserOrigins.Prime && userOrigin !== UserOrigins.Forms);
  const showPreferencesMenuItem = flags && flags['add-automatic-time-stamp-signatures-to-user-preferences'];
  const showBrokerManagementMenuItem = flags && flags['enable-broker-management-link'];
  const [user, setUser] = useState<{ isBrokerAdmin: boolean; isGroupAdmin: boolean }>({
    isBrokerAdmin: false,
    isGroupAdmin: false,
  });

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const componentToRender = (page: string) => {
    return resource === page ? (
      <Typography className={`${classes.navLink} ${classes.selectedNavLink}`}>{transformedResource}</Typography>
    ) : (
      <Link href={`/${page}s`} className={`${classes.navLink} ${classes.unselectedNavLink}`}>
        {transformedString(page)}
      </Link>
    );
  };

  useEffect(() => {
    getUserAdminRoles(setUser, getUsersGroups);
  }, []);

  return (
    <div className={classes.header}>
      <CollapseOnScroll>
        <div className={classes.headerNav}>
          <div className={classes.navLinks}>
            {componentToRender(ResourceNames.ENVELOPE)}
            {areTemplatesEnabled ? (
              componentToRender(ResourceNames.TEMPLATE)
            ) : (
              <StyledTooltip
                title={
                  <>
                    We are hustling to bring
                    <br />
                    you templates soon.
                  </>
                }
                classes={{ tooltip: classes.tooltip }}
                arrow
              >
                <Typography
                  tabIndex={0}
                  className={`${classes.navLink} ${classes.unselectedNavLink}`}
                  style={{ color: colors.grey[500], cursor: 'default' }}
                >
                  Templates
                </Typography>
              </StyledTooltip>
            )}
          </div>
          <div className={classes.navApps}>
            {hideNavItems ? null : <SkyslopeAppsMenu />}
            <UserMenu
              // @ts-expect-error -- This code was written prior to enforcing lint rules
              senderFirstName={senderFirstName}
              // @ts-expect-error -- This code was written prior to enforcing lint rules
              senderLastName={senderLastName}
              showPreferences={showPreferencesMenuItem}
              preferencesUrl={preferencesUrl}
              user={user}
              showBrokerManagement={showBrokerManagementMenuItem}
            />
          </div>
        </div>
      </CollapseOnScroll>
      <div className={classes.navContent}>
        <Typography variant="h3" className={classes.navTitle}>
          {transformedResource}
        </Typography>
        {children}
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default ManagementHeader;
